import React, { useCallback, useRef } from 'react';
import withRetry from '../../../utils/withRetry';
import Search from './Search.web';
import searchPexels, { searchPexelsCuratedImages, searchPexelsWithUrl } from '../../../services/pexels';
import { useDispatch, useSelector } from 'react-redux';
import { createCloudAsset } from '../../../store/cloudAssets/cloudAssets.slice';
import { v4 as uuid } from 'uuid';
import ImageSearchItem from './ImageSearchItem.web';
import { getImageMetadata } from '../../../utils/getImageMetadata';
import {
  addToTimeline,
  addUndo,
  deleteProcessingTimelineItemState,
  removeEmptyTimelineLayers,
  removeUndoRedoById,
  saveVideo,
  setProcessingTimelineItemState,
  updateCanvas,
  updateTimelineItem,
  updateTimelineItemLayer,
} from '../../../store/videoEditor/videoEditor.slice';
import { videoResolutionSeletor } from '../../../store/videoEditor/videoEditor.selectors';
import { getItemCanvasSize } from '../utils/getItemCanvasSize';
import { retryFunctions } from '../../../store/videoEditor/videoEditor.data';
import { getIsItemOnTimeline } from '../utils/getIsItemOnTimeline';
import { StockFilterControls } from './StockFilterControls.web';
import {
  stockFileTypeSelector,
  stockImageResultsSelector,
} from '../../../store/videoEditorLeftSidebar/videoEditorLeftSidebar.selectors';
import { useOrientation } from './utils/useOrientation';
import { setStockImageResults } from '../../../store/videoEditorLeftSidebar/videoEditorLeftSidebar.slice';
import { logError } from '../../../store/appActivity/appActivity.slice';
import { STOCK_FILE_TYPES } from '../videoEditor.constants';
import { FileTypeSelector } from './FileTypeSelector';
import { useIsMounted } from '../../../hooks/useIsMounted';

const StockImages = () => {
  const dispatch = useDispatch();
  const isMountedRef = useIsMounted();

  const resolution = useSelector(videoResolutionSeletor);
  const stockImageResults = useSelector(stockImageResultsSelector);
  const fileType = useSelector(stockFileTypeSelector);

  const { orientation, setOrientation } = useOrientation();

  const requestIdRef = useRef(0);

  const handleSearch = useCallback(
    async ({
      query,
      withReset,
      withDelayedReset,
    }: {
      query: string;
      withReset?: boolean;
      withDelayedReset?: boolean;
    }) => {
      const requestId = ++requestIdRef.current;
      try {
        dispatch(
          setStockImageResults({
            data: withReset ? null : stockImageResults.data,
            next: withReset ? null : stockImageResults.next,
            isLoading: true,
            error: null,
          }),
        );

        const service =
          stockImageResults.next && !(withReset || withDelayedReset)
            ? () => searchPexelsWithUrl(stockImageResults.next)
            : query
            ? () => searchPexels(query, { resultsPerPage: 30, orientation })
            : () => searchPexelsCuratedImages();

        const result = await withRetry(service, {
          errorContext: {
            data: {
              action: 'StockImages.handleSearch',
            },
          },
        });

        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        dispatch(
          setStockImageResults({
            data: withReset || withDelayedReset ? result.photos : [...(stockImageResults.data || []), ...result.photos],
            next: result.next_page,
            isLoading: false,
          }),
        );
      } catch (error) {
        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        dispatch(
          logError({
            event: 'StockImages.handleSearch: error',
            data: {
              error,
            },
          }),
        );

        dispatch(
          setStockImageResults({
            data: withDelayedReset ? [] : stockImageResults.data,
            next: withDelayedReset ? null : stockImageResults.next,
            isLoading: false,
            error,
          }),
        );
      }
    },
    [dispatch, isMountedRef, orientation, stockImageResults.data, stockImageResults.next],
  );

  const getItemKey = useCallback((item) => item.id, []);

  const handleUseItem = useCallback(
    async (item) => {
      const processId = uuid();

      const timelineItemId = uuid();
      dispatch(
        addToTimeline({
          timelineItem: {
            id: timelineItemId,
            type: 'image',
          },
        }),
      );
      dispatch(
        updateTimelineItemLayer({
          timelineItemId,
        }),
      );
      dispatch(removeEmptyTimelineLayers());

      const undoId = uuid();
      dispatch(
        addUndo({
          id: undoId,
        }),
      );

      const processingLabel = 'Stock image';

      const handleFail = (error: any) => {
        dispatch(
          setProcessingTimelineItemState({
            timelineItemId,
            label: processingLabel,
            isProcessing: false,
            isDone: false,
            retryFunctionId,
            error: {
              details: error,
            },
          }),
        );
      };

      const retryFunctionId = uuid();
      const applyItemFunction = async () => {
        try {
          dispatch(
            setProcessingTimelineItemState({
              timelineItemId,
              label: processingLabel,
              isProcessing: true,
              isDone: false,
              retryFunctionId,
            }),
          );

          const itemUrl = item.src.original;

          const response = await fetch(itemUrl);
          const blob = await response.blob();

          const fileMeta = await getImageMetadata({ blob });
          const fileName = itemUrl.split('/').pop().split('?')[0].split('#')[0];
          fileMeta.name = fileName;

          const file = new File([blob], fileName, {
            type: blob.type,
            lastModified: new Date().getTime(),
          });

          dispatch(
            createCloudAsset({
              processId,
              fileType: 'image',
              fileMeta,
              file,
              originalSrc: 'pexels',
              originalData: item,
              onCacheReady: ({ cloudAsset }) => {
                dispatch(
                  removeUndoRedoById({
                    id: undoId,
                  }),
                );

                dispatch(
                  setProcessingTimelineItemState({
                    timelineItemId,
                    label: processingLabel,
                    isProcessing: false,
                    isDone: true,
                  }),
                );
                setTimeout(() => {
                  dispatch(
                    deleteProcessingTimelineItemState({
                      timelineItemId,
                    }),
                  );
                }, 5000);

                const isOnTimeline = getIsItemOnTimeline(timelineItemId);

                if (!isOnTimeline) {
                  return;
                }

                const canvasSize = getItemCanvasSize({
                  width: fileMeta.width,
                  height: fileMeta.height,
                  resolution,
                  scaleUp: true,
                });

                dispatch(
                  updateTimelineItem({
                    id: timelineItemId,
                    cloudAssetId: cloudAsset.id,
                    x: 0,
                    y: 0,
                    width: canvasSize.width,
                    height: canvasSize.height,
                  }),
                );

                dispatch(updateCanvas({}));
                dispatch(
                  addUndo({
                    id: undoId,
                  }),
                );
                dispatch(saveVideo({}));
              },
              onFail: handleFail,
            }),
          );
        } catch (error) {
          handleFail(error);
        }
      };
      retryFunctions[retryFunctionId] = applyItemFunction;
      applyItemFunction();
    },
    [dispatch, resolution],
  );

  const renderItem = useCallback(
    ({ item }) => {
      return (
        <ImageSearchItem url={item.src.medium || item.src.original || null} item={item} onSelect={handleUseItem} />
      );
    },
    [handleUseItem],
  );

  const renderFilterControls = useCallback(
    ({ query }: { query: string }) => {
      return (
        <>
          <FileTypeSelector fileType={fileType} fileTypes={STOCK_FILE_TYPES} />
          <StockFilterControls query={query} orientation={orientation} setOrientation={setOrientation} />
        </>
      );
    },
    [fileType, orientation, setOrientation],
  );

  return (
    <>
      <Search
        results={stockImageResults.data}
        isLoading={stockImageResults.isLoading}
        isError={!!stockImageResults.error}
        hasMore={!!stockImageResults.next}
        withSearchInput
        layout='masonry'
        renderFilterControls={renderFilterControls}
        onSearch={handleSearch}
        renderItem={renderItem}
        getItemKey={getItemKey}
      />
    </>
  );
};
export default StockImages;
